import { useState } from "react";
import { History, X } from "lucide-react";
import { useManufacturerDiscountHistory } from "@/api/hooks";
import type { components } from "@/api/schema";
import { useSaveSourcingOption, type SourcingOption } from "@/api/sourcing";
import { Button } from "@/components/ui/button";
import { POInlineEditableField } from "@/components/po-inline-edit";
import { SearchSelect } from "@/components/search-select";
import { LoadingButton } from "@/components/loading-button";
import {
  DiscountCategoryEditor,
  RemoveDiscountCategory,
} from "@/features/manufacturers/discount-category-editor";
import {
  DiscountChangeList,
  DiscountLastUpdated,
  type DiscountChange,
} from "@/features/manufacturers/discount-history";

type Discount = components["schemas"]["ManufacturerDiscountRead"];

const CHAIN_PATTERN = /^\d+(\.\d+)?(\/\d+(\.\d+)?)*$/;

function chainValid(chain: string): boolean {
  return CHAIN_PATTERN.test(chain.replace(/\s/g, ""));
}

/** The preferred source's discounts: the live AQ categories that price every product. */
export function LiveDiscounts({
  manufacturerUuid,
  discounts,
}: {
  manufacturerUuid: string;
  discounts: Discount[];
}) {
  const history = useManufacturerDiscountHistory(manufacturerUuid);
  const [showHistory, setShowHistory] = useState(false);
  const changesByDiscount = new Map<number, DiscountChange[]>();
  for (const change of history.data ?? []) {
    const list = changesByDiscount.get(change.manufacturer_discount_id) ?? [];
    list.push(change);
    changesByDiscount.set(change.manufacturer_discount_id, list);
  }
  return (
    <section aria-labelledby="live-discounts">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h3 id="live-discounts" className="text-sm font-semibold">
          Discounts
        </h3>
        <div className="flex flex-wrap items-center gap-1">
          <Button
            variant="ghost"
            aria-expanded={showHistory}
            aria-controls="discount-history"
            onClick={() => setShowHistory((value) => !value)}
          >
            <History className="size-4" /> History
          </Button>
          <DiscountCategoryEditor manuUuid={manufacturerUuid} />
        </div>
      </div>
      {showHistory && (
        <div
          id="discount-history"
          className="mb-3 rounded-lg border px-3 py-2.5"
        >
          {history.isPending ? (
            <p className="text-xs text-muted-foreground">Loading history…</p>
          ) : history.isError ? (
            <p role="alert" className="text-xs">
              Could not load the discount history.{" "}
              <Button
                variant="link"
                size="sm"
                className="h-auto px-0 text-xs"
                onClick={() => history.refetch()}
              >
                Retry
              </Button>
            </p>
          ) : history.data.length === 0 ? (
            <p className="text-xs text-muted-foreground">No changes recorded</p>
          ) : (
            <DiscountChangeList
              changes={history.data}
              showName
              label="Discount history"
            />
          )}
        </div>
      )}
      {discounts.length === 0 ? (
        <p className="rounded-lg border px-3 py-2.5 text-sm text-muted-foreground">
          No discount categories
        </p>
      ) : (
        <ul className="divide-y rounded-lg border">
          {discounts.map((discount) => {
            const details = [
              discount.markup ? `Markup ${discount.markup}` : null,
              discount.mfr_status,
              discount.buying_group,
            ].filter(Boolean);
            return (
              <li
                key={discount.id}
                className="flex items-start gap-3 px-3 py-2 text-sm"
              >
                <div className="min-w-0 flex-1">
                  <p className="flex flex-wrap items-baseline gap-x-2">
                    <span className="font-medium">
                      {discount.name || "Unnamed category"}
                    </span>
                    <span className="font-mono text-xs tabular-nums">
                      {discount.discount || "No discount"}
                    </span>
                  </p>
                  {(discount.aq_categories?.length ?? 0) > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {discount.aq_categories
                        ?.map((category) => category.name)
                        .join(", ")}
                    </p>
                  )}
                  {(discount.member_products?.length ?? 0) > 0 && (
                    <p className="text-xs text-muted-foreground">
                      <span className="font-mono">
                        {discount.member_products
                          ?.slice(0, 3)
                          .map((product) => product.model)
                          .join(", ")}
                      </span>
                      {(discount.member_products?.length ?? 0) > 3 &&
                        ` +${(discount.member_products?.length ?? 0) - 3} more`}
                    </p>
                  )}
                  {details.length > 0 && (
                    <p className="text-xs text-muted-foreground">
                      {details.join(" · ")}
                    </p>
                  )}
                  <DiscountLastUpdated
                    changes={changesByDiscount.get(discount.id) ?? []}
                  />
                </div>
                <div className="flex shrink-0">
                  <DiscountCategoryEditor
                    manuUuid={manufacturerUuid}
                    discount={discount}
                  />
                  <RemoveDiscountCategory
                    manuUuid={manufacturerUuid}
                    discount={discount}
                  />
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}

interface NoteDraft {
  key: number;
  categoryId: string;
  label: string;
  chain: string;
}

let noteKey = 0;

/** A backup source's own chain for each live discount category, plus chains not matched yet. */
export function BackupDiscounts({
  manufacturerUuid,
  option,
  categories,
}: {
  manufacturerUuid: string;
  option: SourcingOption;
  categories: Discount[];
}) {
  const saver = useSaveSourcingOption(manufacturerUuid, option.id);
  const initialChains = () =>
    Object.fromEntries(
      categories.map((category) => [
        category.id,
        option.discounts.find(
          (row) => row.manufacturer_discount_id === category.id,
        )?.discount_chain ?? "",
      ]),
    ) as Record<number, string>;
  const initialNotes = () =>
    option.discounts
      .filter((row) => row.manufacturer_discount_id == null)
      .map((row) => ({
        key: ++noteKey,
        categoryId: "none",
        label: row.label ?? "",
        chain: row.discount_chain,
      }));
  const upstreamKey = JSON.stringify([
    option.discounts,
    categories.map((category) => category.id),
  ]);
  const [lastKey, setLastKey] = useState(upstreamKey);
  const [chains, setChains] = useState<Record<number, string>>(initialChains);
  const [notes, setNotes] = useState<NoteDraft[]>(initialNotes);
  if (upstreamKey !== lastKey) {
    setLastKey(upstreamKey);
    setChains(initialChains());
    setNotes(initialNotes());
  }

  const assigned = new Set(
    notes
      .filter((note) => note.categoryId !== "none")
      .map((note) => note.categoryId),
  );
  const body = [
    ...categories
      .filter(
        (category) =>
          (chains[category.id] ?? "").trim() &&
          !assigned.has(String(category.id)),
      )
      .map((category) => ({
        manufacturer_discount_id: category.id,
        label: null,
        discount_chain: (chains[category.id] ?? "").replace(/\s/g, ""),
      })),
    ...notes
      .filter((note) => note.chain.trim())
      .map((note) => ({
        manufacturer_discount_id:
          note.categoryId === "none" ? null : Number(note.categoryId),
        label: note.categoryId === "none" ? note.label.trim() || null : null,
        discount_chain: note.chain.replace(/\s/g, ""),
      })),
  ];
  const valid = body.every((row) => chainValid(row.discount_chain));
  const saved = JSON.stringify(
    option.discounts
      .map((row) => [
        row.manufacturer_discount_id ?? null,
        row.label ?? null,
        row.discount_chain,
      ])
      .sort(),
  );
  const changed =
    JSON.stringify(
      body
        .map((row) => [
          row.manufacturer_discount_id,
          row.label,
          row.discount_chain,
        ])
        .sort(),
    ) !== saved;
  const missing = categories.filter(
    (category) =>
      category.discount &&
      !option.discounts.some(
        (row) => row.manufacturer_discount_id === category.id,
      ),
  );
  const pending = saver.isSaving("discounts");

  return (
    <fieldset className="space-y-2">
      <legend className="mb-2 text-sm font-semibold">Discounts</legend>
      {categories.length > 0 && (
        <ul className="divide-y rounded-lg border">
          {categories.map((category) => (
            <li
              key={category.id}
              className="flex flex-wrap items-center gap-x-3 gap-y-1 px-3 py-2 text-sm"
            >
              <span className="min-w-0 flex-1">
                <span className="block font-medium">
                  {category.name || "Unnamed category"}
                </span>
                <span className="block text-xs text-muted-foreground">
                  Now {category.discount || "no discount"}
                </span>
              </span>
              {/* Click-to-edit into the draft; Save discounts below sends every chain at once (USA-1580). */}
              <POInlineEditableField
                ariaLabel={`${option.supplier?.name ?? "Direct"} discount for ${category.name ?? "category"}`}
                value={chains[category.id] ?? ""}
                placeholder="Not set"
                buttonClassName="w-36 font-mono"
                inputClassName="w-36 font-mono"
                invalid={
                  Boolean(chains[category.id]?.trim()) &&
                  !chainValid(chains[category.id] ?? "")
                }
                onConfirm={(raw) =>
                  setChains((current) => ({
                    ...current,
                    [category.id]: raw,
                  }))
                }
                disabled={pending || assigned.has(String(category.id))}
              />
            </li>
          ))}
        </ul>
      )}
      {notes.length > 0 && (
        <div className="space-y-2">
          <p className="text-xs font-medium text-muted-foreground">
            Recorded, not matched to a category
          </p>
          {notes.map((note) => (
            <div key={note.key} className="flex flex-wrap items-center gap-2">
              <POInlineEditableField
                ariaLabel="Recorded discount chain"
                value={note.chain}
                placeholder="Not set"
                buttonClassName="w-32 font-mono"
                inputClassName="w-32 font-mono"
                invalid={Boolean(note.chain.trim()) && !chainValid(note.chain)}
                onConfirm={(raw) =>
                  setNotes((current) =>
                    current.map((item) =>
                      item.key === note.key ? { ...item, chain: raw } : item,
                    ),
                  )
                }
                disabled={pending}
              />
              <div className="min-w-32 flex-1">
                <POInlineEditableField
                  ariaLabel="Recorded discount covers"
                  value={note.label}
                  placeholder="Covers"
                  buttonClassName="w-full"
                  inputClassName="w-full"
                  onConfirm={(raw) =>
                    setNotes((current) =>
                      current.map((item) =>
                        item.key === note.key ? { ...item, label: raw } : item,
                      ),
                    )
                  }
                  disabled={pending || note.categoryId !== "none"}
                />
              </div>
              <SearchSelect
                ariaLabel="Match to category"
                className="h-9 w-44"
                value={note.categoryId === "none" ? undefined : note.categoryId}
                allLabel="Not matched"
                options={categories.map((category) => ({
                  value: String(category.id),
                  label: category.name || "Unnamed category",
                }))}
                onChange={(value) =>
                  setNotes((current) =>
                    current.map((item) =>
                      item.key === note.key
                        ? { ...item, categoryId: value ?? "none" }
                        : item,
                    ),
                  )
                }
                disabled={pending}
                searchPlaceholder="Search categories"
              />
              <Button
                variant="ghost"
                size="icon"
                className="size-9"
                aria-label="Remove recorded discount"
                onClick={() =>
                  setNotes((current) =>
                    current.filter((item) => item.key !== note.key),
                  )
                }
                disabled={pending}
              >
                <X className="size-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
      {!valid && (
        <p className="text-xs text-destructive">
          Enter discounts as percentages like 50/10/5.
        </p>
      )}
      {missing.length > 0 && (
        <p className="text-xs text-amber-700 dark:text-amber-400">
          Needed before this source can be preferred:{" "}
          {missing.map((category) => category.name).join(", ")}
        </p>
      )}
      <div className="flex items-center justify-between gap-2">
        <span />
        {changed && (
          <LoadingButton
            size="sm"
            loading={pending}
            loadingText="Saving..."
            disabled={!valid}
            onClick={() => saver.save({ discounts: body }, "discounts")}
          >
            Save discounts
          </LoadingButton>
        )}
      </div>
    </fieldset>
  );
}
